import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Fade,
  useTheme,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import axios from "../../../../axios";
import { IP } from "../../../../internalServer";

// Styled Components
const StyledTableRow = styled(TableRow)(({ theme, rank }) => ({
  transition: "all 0.3s ease",
  backgroundColor:
    rank === 1
      ? "rgba(255, 215, 0, 0.08)"
      : rank === 2
        ? "rgba(192, 192, 192, 0.08)"
        : rank === 3
          ? "rgba(205, 127, 50, 0.08)"
          : "inherit",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    transform: "translateX(4px)",
  },
}));

const RankBadge = styled(Box)(({ theme, rank }) => ({
  width: 36,
  height: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
  fontSize: "0.9rem",
  backgroundColor:
    rank === 1
      ? "#FFD700"
      : rank === 2
        ? "#C0C0C0"
        : rank === 3
          ? "#CD7F32"
          : theme.palette.grey[200],
  color: rank <= 3 ? "#fff" : theme.palette.text.secondary,
  boxShadow: rank <= 3 ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
}));

// ✅ FIXED: Student Avatar with gender-based fallback logic
const StudentAvatar = ({ student, size = 40, rank }) => {
  const [imgSrc, setImgSrc] = useState("");
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Construct initial image source based on previous logic
    let initialSrc = "";

    if (student?.profile_picture) {
      // If profile picture exists, prepend IP and remove leading dot
      initialSrc = IP + student.profile_picture.replace(/^\./, "");
    } else if (student?.gender === "M") {
      // Male: check avatarProfile first, else default boy image
      initialSrc = student?.avatarProfile
        ? `/images/Boys/${student.avatarProfile}.webp`
        : "/images/Student_boy_pic.png";
    } else if (student?.gender === "F") {
      // Female: check avatarProfile first, else default girl image
      initialSrc = student?.avatarProfile
        ? `/images/Girls/${student.avatarProfile}.webp`
        : "/images/Student_girl_pic.png";
    } else {
      // Default image for unknown gender
      initialSrc = "/images/default image.png";
    }

    setImgSrc(initialSrc);
    setHasError(false);
  }, [student]);

  const handleError = () => {
    if (hasError) return; // Prevent infinite loop

    // Fallback logic on error (same as previous implementation)
    let fallbackSrc = "";
    if (student?.gender === "M") {
      fallbackSrc = student?.avatarProfile
        ? `/images/Boys/${student.avatarProfile}.webp`
        : "/images/Student_boy_pic.png";
    } else if (student?.gender === "F") {
      fallbackSrc = student?.avatarProfile
        ? `/images/Girls/${student.avatarProfile}.webp`
        : "/images/Student_girl_pic.png";
    } else {
      fallbackSrc = "/images/default image.png";
    }

    setImgSrc(fallbackSrc);
    setHasError(true);
  };

  const borderColor =
    rank === 1
      ? "#FFD700"
      : rank === 2
        ? "#C0C0C0"
        : rank === 3
          ? "#CD7F32"
          : "transparent";

  return (
    <Avatar
      src={imgSrc}
      onError={handleError}
      sx={{
        width: size,
        height: size,
        border: rank <= 3 ? `3px solid ${borderColor}` : "2px solid #e0e0e0",
        boxShadow:
          rank <= 3
            ? `0 4px 12px ${borderColor}80`
            : "0 2px 4px rgba(0,0,0,0.1)",
        backgroundColor: "#f5f5f5",
      }}
    >
      {student?.name?.charAt(0) || "S"}
    </Avatar>
  );
};

const LeaderBoard = ({
  leaderBoardData,
  setClick,
  subject,
  leaderBoardPData,
  assessmentId,
  isAdaptive,
}) => {
  const theme = useTheme();
  const [leaderboardStats, setLeaderboardStats] = useState({});

  useEffect(() => {
    const fetchLeaderboardStats = async () => {
      try {
        const response = await axios.get(
          `/api/v1/assessment/${assessmentId}/LeaderBoardAggStats/get`,
        );
        setLeaderboardStats(response.data.data);
      } catch (error) {
        console.error("Error fetching leaderboard stats:", error);
      }
    };
    fetchLeaderboardStats();
  }, [assessmentId]);

  // Sort data by score (descending)
  const sortedData = useMemo(() => {
    if (!leaderBoardData || !Array.isArray(leaderBoardData)) return [];

    return [...leaderBoardData]
      .sort((a, b) => {
        const scoreA = isAdaptive ? a.ability || 0 : a.obtained_marks || 0;
        const scoreB = isAdaptive ? b.ability || 0 : b.obtained_marks || 0;
        return scoreB - scoreA;
      })
      .map((item, index) => ({
        ...item,
        computedRank: index + 1,
      }));
  }, [leaderBoardData, isAdaptive]);

  const topThree = sortedData.slice(0, 3);
  const restOfData = sortedData.slice(3);

  return (
    <Fade in={true}>
      <Box
        className="transprant_body_finalResult"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
          zIndex: 1300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 1, sm: 2, md: 3 },
          overflow: "auto",
        }}
      >
        <Paper
          elevation={24}
          sx={{
            width: "100%",
            maxWidth: 900,
            maxHeight: "90vh",
            overflow: "auto",
            borderRadius: 3,
            position: "relative",
            background: "linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setClick(false)}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              backgroundColor: "rgba(0,0,0,0.04)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.08)",
                transform: "rotate(90deg)",
              },
              transition: "all 0.3s",
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Header */}
          <Box
            sx={{
              p: 4,
              pb: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <EmojiEventsIcon sx={{ fontSize: 35 }} />
              Leaderboard
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Chip
                label={subject || "General"}
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {sortedData.length} Students
              </Typography>
            </Box>
          </Box>

          {/* Top 3 Podium */}
          {topThree.length > 0 && (
            <Box sx={{ px: 4, py: 4 }}>
              <Grid
                container
                spacing={2}
                alignItems="flex-end"
                justifyContent="center"
              >
                {/* 2nd Place - Left */}
                {topThree[1] && (
                  <Grid item xs={4} sx={{ textAlign: "center", pb: 1 }}>
                    <Fade in={true} style={{ transitionDelay: "100ms" }}>
                      <Box
                        sx={{
                          background:
                            "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
                          borderRadius: 3,
                          p: 2,
                          border: "2px solid #C0C0C0",
                          boxShadow: "0 6px 20px rgba(192,192,192,0.25)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                            mb: 1.5,
                          }}
                        >
                          <RankBadge rank={2}>2</RankBadge>
                          <StudentAvatar
                            student={topThree[1].student}
                            size={55}
                            rank={2}
                          />
                        </Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          noWrap
                          sx={{ maxWidth: 100, mx: "auto" }}
                        >
                          {topThree[1].student?.name || "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight="bold"
                        >
                          {isAdaptive
                            ? topThree[1].ability?.toFixed(2)
                            : topThree[1].obtained_marks}{" "}
                          pts
                        </Typography>
                      </Box>
                    </Fade>
                  </Grid>
                )}

                {/* 1st Place - Center (Highlighted) */}
                {topThree[0] && (
                  <Grid item xs={4} sx={{ textAlign: "center", pb: 1 }}>
                    <Fade in={true}>
                      <Box
                        sx={{
                          background:
                            "linear-gradient(180deg, #fff9e6 0%, #ffe4b5 100%)",
                          borderRadius: 3,
                          p: 2.5,
                          border: "3px solid #FFD700",
                          boxShadow: "0 8px 30px rgba(255,215,0,0.35)",
                          transform: "scale(1.05)",
                        }}
                      >
                        <EmojiEventsIcon
                          sx={{ fontSize: 28, color: "#FFD700", mb: 0.5 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <RankBadge
                            rank={1}
                            sx={{ width: 42, height: 42, fontSize: "1rem" }}
                          >
                            1
                          </RankBadge>
                          <StudentAvatar
                            student={topThree[0].student}
                            size={70}
                            rank={1}
                          />
                        </Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          noWrap
                          sx={{ maxWidth: 110, mx: "auto" }}
                        >
                          {topThree[0].student?.name || "N/A"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="primary"
                          fontWeight="bold"
                        >
                          {isAdaptive
                            ? topThree[0].ability?.toFixed(2)
                            : topThree[0].obtained_marks}{" "}
                          pts
                        </Typography>
                      </Box>
                    </Fade>
                  </Grid>
                )}

                {/* 3rd Place - Right */}
                {topThree[2] && (
                  <Grid item xs={4} sx={{ textAlign: "center", pb: 1 }}>
                    <Fade in={true} style={{ transitionDelay: "200ms" }}>
                      <Box
                        sx={{
                          background:
                            "linear-gradient(180deg, #faf5f0 0%, #f0e6d8 100%)",
                          borderRadius: 3,
                          p: 2,
                          border: "2px solid #CD7F32",
                          boxShadow: "0 6px 20px rgba(205,127,50,0.25)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                            mb: 1.5,
                          }}
                        >
                          <RankBadge rank={3}>3</RankBadge>
                          <StudentAvatar
                            student={topThree[2].student}
                            size={55}
                            rank={3}
                          />
                        </Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          noWrap
                          sx={{ maxWidth: 100, mx: "auto" }}
                        >
                          {topThree[2].student?.name || "N/A"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight="bold"
                        >
                          {isAdaptive
                            ? topThree[2].ability?.toFixed(2)
                            : topThree[2].obtained_marks}{" "}
                          pts
                        </Typography>
                      </Box>
                    </Fade>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          {/* Table */}
          <Box sx={{ px: 3, pb: 3 }}>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: "bold", width: 80 }}>
                      Rank
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Student</TableCell>
                    {!isAdaptive && (
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Best Score
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ fontWeight: "bold" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        {isAdaptive ? "Ability Score" : "Obtained Marks"}
                        <TrendingUpIcon fontSize="small" color="action" />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((student) => (
                    <StyledTableRow
                      key={student.id}
                      rank={student.computedRank}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <RankBadge rank={student.computedRank}>
                            {student.computedRank}
                          </RankBadge>
                          {student.computedRank <= 3 && (
                            <EmojiEventsIcon
                              fontSize="small"
                              sx={{
                                color:
                                  student.computedRank === 1
                                    ? "#FFD700"
                                    : student.computedRank === 2
                                      ? "#C0C0C0"
                                      : "#CD7F32",
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          {/* ✅ Using StudentAvatar with full student object */}
                          <StudentAvatar
                            student={student.student}
                            size={40}
                            rank={student.computedRank}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {student.student?.name || "Unknown"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Roll: {student.student?.roll_number || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {!isAdaptive && (
                        <TableCell align="center">
                          <Chip
                            label={
                              student.student?.bestScore ||
                              student.obtained_marks ||
                              0
                            }
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          color={
                            student.computedRank <= 3
                              ? "primary"
                              : "text.primary"
                          }
                        >
                          {isAdaptive
                            ? student.ability?.toFixed(2) || "0.00"
                            : student.obtained_marks || 0}
                        </Typography>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {sortedData.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.secondary">
                  No data available
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default LeaderBoard;
